import { ApiProject } from "@incendium/api";
import { Box, LinearProgress, Stack, Typography, useTheme } from "@mui/material";
import GlassCard from "Components/GlassCard/GlassCard";
import Loading from "Components/Loading/Loading";
import { formatNumber } from "Helpers/numbers";
import Image from "mui-image";
import { useEffect, useMemo, useState } from "react";

const messageBollocks = [
  "Scanning homepage structure...",
  "Detecting primary calls-to-action...",
  "Identifying navigation hierarchy...",
  "Mapping customer journeys...",
  "Evaluating visual hierarchy...",
  "Analyzing internal links...",
  "Running semantic content scan...",
  "Compiling site summary...",
];

interface IOnboardingScanningProps {
  project: ApiProject;
  onShown: () => void;
}

function OnboardingScanning({ project, onShown }: IOnboardingScanningProps) {
  const [progress, setProgress] = useState(0);
  const theme = useTheme();
  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((oldProgress) => {
        if (oldProgress === 100) {
          return 0;
        }
        const diff = Math.random() * 10;
        const next = Math.min(oldProgress + diff, 100);
        if (next === 100) {
          clearInterval(timer);
        }
        return next;
      });
    }, 250);

    return () => {
      clearInterval(timer);
    };
  }, []);

  const currentMessage = useMemo(() => {
    const idx = Math.floor((progress / 100) * messageBollocks.length);
    const safeIdx = Math.min(idx, messageBollocks.length - 1);
    return messageBollocks[safeIdx];
  }, [progress]);

  const homepageScreen = useMemo(() => {
    return project.domains?.find(d => d.isPrimary)?.screenshot?.files?.[0];
  }, [project]);

  useEffect(() => {
    if (progress >= 100) {
      setTimeout(() => {
        onShown();
      }, 200);
    }
  }, [progress, onShown]);

  return (
    <Box sx={{ overflow: "hidden", height: 450, position: "relative" }}>
      <GlassCard sx={{ height: "100%", width: "100%" }} boxProps={{ pb: 3 }}>
        <LinearProgress variant="determinate" value={progress} />
        <Box
          mt={1}
          sx={{ opacity: progress & 1 ? 0.7 : 1, transition: "all 0.2s", height: "100%", width: "100%", backgroundColor: theme.palette.background.default }}
        >
          <Image
            src={homepageScreen?.fullPath || ""}
            duration={50}
          />
        </Box>
      </GlassCard>
      <GlassCard
        boxProps={{ p: 2 }}
        sx={{ width: 360, height: 140, position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}
      >
        <Stack
          direction={"row"}
          spacing={2}
          justifyContent="center"
          alignItems={"center"}
        >
          <Loading text={`${formatNumber(progress)}%`} size={48} />
        </Stack>

        <Typography variant="subtitle1">{currentMessage}</Typography>
      </GlassCard>

    </Box>
  );
}

export default OnboardingScanning;
